import axios from '@/lib/axios'
import { useCompaniesStore } from '@/stores/companies'
import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, watch } from 'vue'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { PaginatedResponse } from '@/interfaces/paginatedResponse'

import { Company } from '../interfaces/company'

interface Config {
	immediate?: boolean
	errorHandling?: boolean
}
interface getCompanyRequest {
	corporate?: String
	company?: String
	includes?: String[]
	page?: number
	errorHandling?: boolean
}

const getCompanies = async (params?: getCompanyRequest) => {
	const { data } = await axios.get<PaginatedResponse<Company>>(
		`/api/companies?page=${params?.page}`,
		{
			params: {
				corporationId: params?.corporate,
				company: params?.company,
			},
			errorHandling: params?.errorHandling,
		} as CustomAxiosRequestConfig
	)

	return data
}

const useCompanies = (payload: getCompanyRequest, config: Config = {}) => {
	const { immediate = true, errorHandling = false } = config
	const { setCompanies, setPage } = useCompaniesStore()
	const { companies, page } = storeToRefs(useCompaniesStore())

	const { data, isError, isLoading, refetch, isFetching, isSuccess } = useQuery(
		['companies', payload?.company, payload?.corporate, page],
		{
			queryFn: async () =>
				await getCompanies({
					company: payload.company,
					corporate: payload.corporate,
					page: page.value,
					errorHandling,
				}),
			enabled: immediate,
			retry: errorHandling,
		}
	)

	watch(
		data,
		(value) => {
			if (value) {
				setCompanies(value.data)
			}
		},
		{
			immediate,
			deep: true,
		}
	)

	return {
		data,
		companies,
		isError,
		isLoading,
		isSuccess,
		isFetching,
		page,

		searchParams() {
			refetch()
		},
		refetch,
		setPage,
	}
}

export default useCompanies
